import React from 'react';

import { Card, CardBody, CardHeader } from '@change/ds-core-ui';
import { FacebookIcon, RedditIcon } from '@change/ds-icons';
import { cn } from '@change/ds-utils';

type Platform = 'facebook' | 'reddit';

type PlaceholderSocialCardProps = Readonly<{
	platform: Platform;
	isReskin?: boolean;
}>;

/**
 * Placeholder social card component for empty states.
 * Displays a non-interactive card with shimmer-style placeholder content.
 * Uses gray color (#E1DDE9) for all placeholder elements.
 *
 * ```tsx
 * <PlaceholderSocialCard platform="facebook" isReskin />
 * ```
 */
export const PlaceholderSocialCard: React.FC<PlaceholderSocialCardProps> = ({ platform, isReskin = false }) => {
	const PLACEHOLDER_COLOR = 'bg-[#E1DDE9]';

	const platformConfig = {
		facebook: {
			Icon: FacebookIcon,
			bgColor: 'bg-[#0866FF]',
			iconColor: 'text-white',
		},
		reddit: {
			Icon: RedditIcon,
			bgColor: 'bg-[#FF4500]',
			iconColor: 'text-white',
		},
	};

	const { Icon, bgColor, iconColor } = platformConfig[platform];

	return (
		<div
			data-platform={platform}
			data-testid={`placeholder-social-card-${platform}`}
			className="relative snap-center"
			aria-hidden="true"
		>
			{/* Social icon badge positioned in top-right corner, outside the card */}
			<div
				className={cn(
					'absolute -top-[3px] -right-[3px] z-10 flex h-8 w-8 items-center justify-center rounded-full border border-solid',
					bgColor,
				)}
				data-testid={`${platform}-icon-badge`}
			>
				<Icon className={iconColor} size={20} />
			</div>

			<Card
				className={cn(
					'elevation-low orientation-vertical:w-[125px] flex h-full max-h-[160px] flex-col border-[1.5px] [--card-gap:0px]',
					{
						'rounded-xl': isReskin,
					},
				)}
				orientation="vertical"
			>
				<>
					<CardHeader variant="media" className="h-[104px]">
						<div className={cn('h-min-[104px] flex h-full w-full items-center justify-center', PLACEHOLDER_COLOR)} />
					</CardHeader>

					<CardBody className="flex flex-col gap-1.5 px-3 pt-3">
						<div className={cn('h-[18px] w-full rounded', PLACEHOLDER_COLOR)} />
						<div className={cn('h-[14px] w-4/5 rounded', PLACEHOLDER_COLOR)} />
					</CardBody>
				</>
			</Card>
		</div>
	);
};
